/**
 * ⟦C57 · lot 2 · A-174⟧ **Les événements, en court.** Tout ce que la fenêtre d'événement et le
 * Journal affichent d'un événement passe par ce module : le texte COURT quand le contenu en
 * porte un (`titre_court`, `texte_court`, `label_court`, `morale_courte`), le long sinon, et le
 * détail (le long, les indices, la parade) rangé derrière « Les dessous ».
 *
 * Matt, 02/10/2026 : *« Il y a trop de trucs écrits partout, on ne lit plus rien à la fin. »* La
 * fenêtre d'événement est le dialogue le plus vu du jeu (37 événements en 242 actions, 467 par
 * partie complète). À l'écran : un titre de 5 mots au plus, une situation de 12 mots au plus, un
 * choix de 6 mots au plus, une morale de 12 mots au plus. **Rien n'est supprimé** : le texte
 * long de `content/events.json` ne bouge pas, il s'ouvre derrière « Les dessous » et se relit
 * dans le Journal.
 *
 * ## Ce que ce module ne fait PAS
 *
 * Il ne lit jamais `effets` pour télégraphier un choix. ⟦SPEC-UI §3.6⟧ : « les effets d'un choix
 * ne sont affichés que s'ils sont fournis par l'API (`hint`) ; le contenu décide de ce qui est
 * télégraphié » ; ⟦A-49⟧ confirme sans lever. Les icônes et les nombres remplacent les phrases
 * là où une phrase existait (l'avertissement de strike : `⛔ +1 strike (2/3)`), pas ailleurs :
 * dérouler les effets de 352 choix reviendrait à rendre le jeu lisible d'avance, et c'est un
 * arbitrage de Matt, pas un détail d'écran.
 *
 * Pur : aucun DOM, aucun store. Dépend du contenu (`contenu()`), comme `journal.ts`.
 */

import { STRIKES_FATAL, type EventChoice, type GameEvent } from "@maxime/shared";

import { contenu } from "./content";
import type { BlocSource } from "./journal";

// ─────────────────────────────────────────────────────────────────────────────
// Les textes à l'écran : le court, sinon le long
// ─────────────────────────────────────────────────────────────────────────────

/** Emoji par catégorie d'événement (placeholder §8.1 : `content/events.json` n'a pas d'`icon`). */
export const ICONE_CATEGORIE: Record<string, string> = {
  viralite: "🚀",
  good_buzz: "✨",
  bad_buzz: "🔥",
  drama: "⚔️",
  plateforme: "🛰️",
  materiel: "🔧",
  communaute: "🤝",
  business: "💼",
  perso: "🏠",
  bibliotheque: "📚",
  alignement: "🎭",
  opportunite: "🎟️",
};

export function iconeCategorie(categorie: string): string {
  return ICONE_CATEGORIE[categorie] ?? "📨";
}

/**
 * ⟦C62 · lot événements · A-179⟧ **Le mot de joueur d'une catégorie.** Le bandeau de la fenêtre lisait
 * l'identifiant du contenu (`bad_buzz` : « BAD BUZZ », `communaute` : « COMMUNAUTE » sans accent) : un mot de
 * développeur, devant le premier événement du jeu. `content/meta.json → evenements_ecran.libelles_categories`
 * dit le mot de chaque catégorie ; une catégorie absente (ou un contenu d'avant) garde son identifiant, les
 * soulignés en espaces, comme avant le chantier.
 */
export function libelleCategorie(categorie: string): string {
  const mots = contenu().meta.evenements_ecran?.libelles_categories as Partial<Record<string, string>> | undefined;
  return mots?.[categorie] ?? categorie.replace(/_/g, " ");
}

/**
 * ⟦C62 · lot événements · A-179⟧ **Les réglages d'écran de la fenêtre d'événement** (`content/meta.json →
 * evenements_ecran`) : tout est éteint quand le contenu n'en dit rien, l'écran d'avant, au caractère près.
 */
export function reglagesEvenementsEcran(): { masquerVerrouillesAvantArbre: boolean; consequencesVisibles: boolean } {
  const r = contenu().meta.evenements_ecran;
  return {
    masquerVerrouillesAvantArbre: r?.masquer_verrouilles_avant_arbre ?? false,
    consequencesVisibles: r?.consequences_visibles ?? false,
  };
}

/** Le titre affiché : 5 mots au plus quand le contenu l'a écrit court. */
export function titreCourt(e: Pick<GameEvent, "nom" | "titre_court">): string {
  return e.titre_court ?? e.nom;
}

/** La situation affichée : 12 mots au plus quand le contenu l'a écrite courte. */
export function texteCourt(e: Pick<GameEvent, "texte" | "texte_court">): string {
  return e.texte_court ?? e.texte;
}

/** Le libellé affiché d'un choix : 6 mots au plus quand le contenu l'a écrit court. */
export function labelCourt(c: Pick<EventChoice, "label" | "label_court">): string {
  return c.label_court ?? c.label;
}

/** La morale affichée dans la fenêtre « c'est réglé » : 12 mots au plus, ou `undefined` sans morale. */
export function moraleCourte(e: Pick<GameEvent, "morale" | "morale_courte">): string | undefined {
  return e.morale === undefined ? undefined : (e.morale_courte ?? e.morale);
}

/**
 * ⟦C57 · lot 2⟧ Un texte rendu en UNE phrase, pour une ligne du Journal (« une phrase de 12 mots au plus ») :
 * sans guillemets, sans point final, et les phrases d'un libellé liées par des virgules (« Te repasser la vidéo.
 * Deux fois. » devient « Te repasser la vidéo, deux fois »). Le nombre de mots ne change pas ; le texte entier,
 * lui, est dans « Les dessous ».
 */
export function enUneLigne(texte: string): string {
  return texte
    .replace(/[«»]/gu, "")
    .replace(/\s+/gu, " ")
    .trim()
    .replace(/[\s.!?…]+$/u, "")
    .replace(/\s*[.!?…]+\s+(\p{Lu})(?=\p{Ll})/gu, (_m, c: string) => `, ${c.toLowerCase()}`)
    .replace(/\s*[.!?…]+\s+/gu, ", ");
}

/** Les paragraphes d'un texte long (une ligne vide les sépare : « Le Grand Saut » en a deux). */
export function paragraphes(texte: string): string[] {
  return texte
    .split(/\n{2,}/u)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

/** Le contenu a-t-il écrit quelque chose de plus court que le long, pour cet événement ? */
export function estRaccourci(e: GameEvent): boolean {
  return (
    e.titre_court !== undefined ||
    e.texte_court !== undefined ||
    (e.choix ?? []).some((c) => c.label_court !== undefined)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Le strike : une icône et des nombres à la place de la phrase
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Ce que le bouton d'un choix qui pose un strike dit à l'écran : `⛔ +1 strike (2/3)`, ou
 * `⛔ 3/3 : la chaîne saute`. C'est l'AVERTISSEMENT, la seule conséquence écrite sur un bouton
 * (« prévenir, jamais surprendre », ⟦PLAN §4.9⟧) : il reste, en icône et en nombres.
 */
export function strikeCourt(strikes: number, total: number, fatal: number = STRIKES_FATAL): string {
  if (total >= fatal) return `⛔ ${total}/${fatal} : la chaîne saute`;
  return `⛔ +${strikes} strike${strikes > 1 ? "s" : ""} (${total}/${fatal})`;
}

/** La même chose en toutes lettres : c'est elle qu'on lit derrière « Les dessous » et à la voix. */
export function strikeLong(strikes: number, total: number, fatal: number = STRIKES_FATAL): string {
  return (
    `${strikes > 1 ? `${strikes} strikes` : "Un strike"} : tu passerais à ${total} sur ${fatal}. ` +
    (total >= fatal ? "La chaîne saute." : `À ${fatal}, la chaîne saute.`)
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// « Les dessous » de la fenêtre d'événement : ce que l'écran ne dit plus
// ─────────────────────────────────────────────────────────────────────────────

export interface ContexteAvantChoix {
  /** Les choix cliquables, dans l'ordre de la liste (la touche 1, 2, 3…). */
  disponibles: readonly EventChoice[];
  /** Les choix hors de portée (le nœud d'arbre qui manque), listés sous les autres. */
  verrouilles: readonly { choix: EventChoice; nomDuNoeud: string }[];
  /** Combien de strikes pose chaque choix (id du choix → nombre), et le total actuel. */
  strikes: ReadonlyMap<string, number>;
  strikesActifs: number;
  /** Le joueur possède-t-il déjà la parade (le nœud de `contremesure`) ? */
  paradePossedee: boolean;
}

/**
 * Les blocs de « Les dessous » AVANT le choix : la situation en entier, les choix en entier (avec
 * leurs indices et l'avertissement de strike en toutes lettres), la parade. Chaque bloc n'existe
 * que s'il dit quelque chose que l'écran ne dit plus : jamais un bloc qui répète.
 */
export function blocsAvantChoix(e: GameEvent, ctx: ContexteAvantChoix): BlocSource[] {
  const blocs: BlocSource[] = [];

  if (estRaccourci(e)) blocs.push({ titre: "La situation", paragraphes: paragraphes(e.texte) });

  const aDuDetail =
    ctx.disponibles.some(
      (c) => c.label_court !== undefined || c.hint !== undefined || (ctx.strikes.get(c.id) ?? 0) > 0,
    ) || ctx.verrouilles.some((v) => v.choix.label_court !== undefined);
  if (aDuDetail) {
    blocs.push({
      titre: "Les choix",
      lignes: [
        ...ctx.disponibles.map((c, i) => {
          const n = ctx.strikes.get(c.id) ?? 0;
          const piece = [c.label, c.hint, n > 0 ? `⛔ ${strikeLong(n, ctx.strikesActifs + n)}` : undefined];
          return { icone: String(i + 1), texte: piece.filter((x) => x !== undefined && x !== "").join(" : ") };
        }),
        ...ctx.verrouilles.map((v) => ({ icone: "🔒", texte: `${v.choix.label} : nécessite ${v.nomDuNoeud}.` })),
      ],
    });
  }

  const parade = e.contremesure;
  if (parade) {
    const nom = contenu().parSkill.get(parade.skill_id)?.nom ?? parade.skill_id;
    const debloque = parade.choix_debloque ? (e.choix ?? []).find((c) => c.id === parade.choix_debloque) : undefined;
    blocs.push({
      titre: "La parade",
      lignes: [
        {
          icone: "🛡",
          texte: ctx.paradePossedee ? `${nom} : ta parade est en place.` : `${nom} : la parade existe, tu ne l'as pas encore.`,
        },
        debloque ? { icone: "🔓", texte: `Elle débloque « ${debloque.label} ».` } : null,
        parade.poids_mult < 1 ? { icone: "🎲", texte: "Avec elle, cette tuile tombe moins souvent." } : null,
      ],
    });
  }

  return blocs;
}

// ─────────────────────────────────────────────────────────────────────────────
// Le Journal : ce qui s'est passé, en une ligne, et le reste derrière « Les dessous »
// ─────────────────────────────────────────────────────────────────────────────

/** Une ligne de Journal à détail, en données pures : `journal.ts` en fait une `JournalEntry`. */
export interface LigneEvenement {
  icone: string;
  ton: "info";
  texte: string;
  /** Le sujet du détail : le titre LONG de l'événement. */
  sujet: string;
  blocs: BlocSource[];
}

/** La situation, le choix et la morale, en entier : ce que la ligne courte ne dit plus. */
export function blocsApresChoix(e: GameEvent, choixId: string | null): BlocSource[] {
  const choix = choixId ? (e.choix ?? []).find((c) => c.id === choixId) : undefined;
  const blocs: (BlocSource | null)[] = [
    { titre: "La situation", paragraphes: paragraphes(e.texte) },
    choix ? { titre: "Ton choix", paragraphes: [choix.label, choix.hint] } : null,
    e.morale ? { titre: "La morale", paragraphes: [e.morale] } : null,
  ];
  return blocs.filter((b): b is BlocSource => b !== null);
}

/**
 * Le fait « le joueur a tranché » : `Le Grand Saut : Poser ta démission` (5 + 6 mots au plus, donc
 * jamais plus de 12), et la situation, le choix long et la morale longue derrière « Les dessous ».
 */
export function ligneChoix(eventId: string, choixId: string): LigneEvenement | null {
  const e = contenu().parEvenement.get(eventId);
  if (!e) return null;
  const choix = (e.choix ?? []).find((c) => c.id === choixId);
  return {
    icone: iconeCategorie(e.categorie),
    ton: "info",
    texte: choix ? `${enUneLigne(titreCourt(e))} : ${enUneLigne(labelCourt(choix))}` : enUneLigne(titreCourt(e)),
    sujet: e.nom,
    blocs: blocsApresChoix(e, choixId),
  };
}

/** Un événement-notification (`issue`, sans `choix`) : il s'applique au tirage, la ligne dit la situation. */
export function estNotification(e: GameEvent): boolean {
  return e.choix === undefined && e.issue !== undefined;
}

/**
 * Le fait « une notification est tombée » : la situation courte à la ligne ; la situation entière,
 * ce que le personnage en a fait (`issue.label`) et la morale derrière « Les dessous ».
 */
export function ligneNotification(eventId: string): LigneEvenement | null {
  const e = contenu().parEvenement.get(eventId);
  if (!e || !estNotification(e)) return null;
  const blocs: (BlocSource | null)[] = [
    { titre: "La situation", paragraphes: paragraphes(e.texte) },
    { titre: "Ce que tu as fait", paragraphes: [e.issue?.label] },
    e.morale ? { titre: "La morale", paragraphes: [e.morale] } : null,
  ];
  return {
    icone: iconeCategorie(e.categorie),
    // « info » : le moteur l'écrivait déjà en trame « info » (pas de toast), la ligne courte ne change pas ça.
    ton: "info",
    texte: enUneLigne(texteCourt(e)),
    sujet: e.nom,
    blocs: blocs.filter((b): b is BlocSource => b !== null),
  };
}

/**
 * Le fait « une fenêtre s'ouvre » (une offre qui expire) : `Une fenêtre s'ouvre : « Titre »,
 * jusqu'au jour 12.` à la ligne ; l'offre entière et le délai derrière « Les dessous ».
 */
export function ligneFenetre(eventId: string, jourLimite: string): LigneEvenement | null {
  const e = contenu().parEvenement.get(eventId);
  if (!e) return null;
  return {
    icone: "🎟️",
    ton: "info",
    texte: `Une fenêtre s'ouvre : « ${enUneLigne(titreCourt(e))} », jusqu'au jour ${jourLimite}.`,
    sujet: e.nom,
    blocs: [
      { titre: "L'offre", paragraphes: paragraphes(e.texte) },
      {
        titre: "Le délai",
        paragraphes: [`Tu as jusqu'au jour ${jourLimite} pour répondre. Après, ils prennent quelqu'un d'autre.`],
      },
    ],
  };
}

/**
 * « Je gère » et « le manager tranche » : la ligne dit QUI a tranché, en moins de douze mots, et
 * l'explication (l'issue par défaut, le moment) rejoint « Les dessous ».
 */
export function ligneDelegation(
  eventId: string,
  choixId: string | null,
  qui: { type: "report" } | { type: "manager"; nomManager: string },
): LigneEvenement | null {
  const e = contenu().parEvenement.get(eventId);
  if (!e) return null;
  const titre = enUneLigne(titreCourt(e));
  const explication =
    qui.type === "report"
      ? "Tu as remis cet événement à plus tard : il se règle sans toi, à l'issue par défaut."
      : `${qui.nomManager} a tranché à ta place, pendant que tu étais ailleurs.`;
  return {
    icone: "⏳",
    ton: "info",
    texte: qui.type === "report" ? `Tu gères : « ${titre} ».` : `${qui.nomManager} tranche « ${titre} ».`,
    sujet: e.nom,
    blocs: [{ titre: "Ce qui s'est passé", paragraphes: [explication] }, ...blocsApresChoix(e, choixId)],
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Les lignes que le moteur écrit lui-même (`log`) et que le Journal remplace
// ─────────────────────────────────────────────────────────────────────────────

/** Ce que `entreesDeResolution` sait des faits d'événement d'une résolution (ids seulement). */
export interface FaitsEvenement {
  /** `event_resolved` d'un choix : un choix du joueur, de « Je gère » ou du manager. */
  resolus: readonly { event_id: string; choice_id: string }[];
  /** `event_drawn` : un événement tiré. `pending` : il bloque ; `opportunite` : une offre à fenêtre. */
  tires: readonly { event_id: string; pending: boolean; opportunite: boolean }[];
  /** `warning` « Je gère » (`evenement_reporte`) et « le manager tranche » (`manager_tranche`). */
  delegues: readonly { event_id: string }[];
}

/**
 * Le texte exact que le moteur écrit au journal pour une notification (`events.ts`, `tirerEvenement`) :
 * la situation, ce que le personnage en a fait et la morale, collés. Il sert à RECONNAÎTRE la ligne
 * pour la remplacer par la ligne courte à détail ; une ligne qui ne correspond plus (le moteur a
 * changé son texte) reste simplement affichée : on ne perd rien, on ne court-circuite rien.
 */
export function texteLogNotification(e: GameEvent): string {
  return [e.texte, e.issue?.label, e.morale].filter(Boolean).join(" ");
}

/**
 * Le texte d'une ligne de trame écrite par le moteur autour d'un événement, tel que le Journal le
 * garde. `null` = la ligne est remplacée par une ligne courte à détail (même fait, même information,
 * rangée derrière « Les dessous »), on ne l'écrit pas deux fois ; une chaîne = le texte à garder
 * (le titre court remplace un titre trop long) ; `undefined` = pas une ligne d'événement, inchangée.
 */
export function adapterLigneDeTrame(texte: string, faits: FaitsEvenement): string | null | undefined {
  const parEvenement = contenu().parEvenement;

  // « Choix : « … ». C'est fait, on avance. » : la ligne courte du fait `event_resolved` la remplace.
  if (/^Choix : « /u.test(texte) && /C'est fait, on avance\.$/u.test(texte) && faits.resolus.length > 0) return null;

  for (const d of faits.delegues) {
    const e = parEvenement.get(d.event_id);
    if (!e) continue;
    const nom = e.nom;
    // Les deux phrases du moteur (`events.ts`) : la ligne courte « Tu gères / le manager tranche » les remplace.
    if (texte === `Tu remets « ${nom} » à plus tard. L'issue par défaut se joue sans toi.`) return null;
    if (texte === `Pendant que tu étais ailleurs, ton manager tranche « ${nom} » à ta place.`) return null;
  }

  for (const t of faits.tires) {
    const e = parEvenement.get(t.event_id);
    if (!e) continue;
    const nom = e.nom;
    if (t.pending && texte === `Événement : « ${nom} ». À toi de trancher.`) {
      return e.titre_court !== undefined ? `Événement : « ${e.titre_court} ». À toi de trancher.` : undefined;
    }
    // L'annonce d'une offre : « Une fenêtre s'ouvre… » (journal.ts) dit déjà tout, derrière ses dessous.
    if (t.opportunite && texte.startsWith(`Une porte s'entrouvre : « ${nom} ».`)) return null;
    if (estNotification(e) && texte === texteLogNotification(e)) return null;
  }

  return undefined;
}
